import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { LazyLoadEvent, MessageService } from 'primeng/api';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { capitalizeWords } from 'src/assets/functions/capitalize-words';
import { createWorkbookBuffer } from 'src/assets/functions/exceljs-export';
import { FormatOdometerNumber } from 'src/assets/functions/format-odometer-number';
import { getHeaderMapDealer } from 'src/assets/functions/header-map-excel-dealer';
import { maskCpfAndCnpj } from 'src/assets/functions/information-masks';
import { fileTypeTicket, listStatusType } from 'src/assets/functions/list-status-type';
import { OnlyAcceptNumbers } from 'src/assets/functions/only-accept-numbers';
import { validationCnpj, validationCpf } from 'src/assets/validations/cpf_cnpj_validation';
import { ApsPermitedScreen } from '../../models/aps-permission/aps-permited-screen';
import { ConstantsFordProtect } from '../../models/constants/constants-ford-protect';
import { ContractCancelInput } from '../../models/contract/contract-cancel-input';
import { ExportContractFromExcelDealer } from '../../models/contract/export-contract-from-excel-dealer';
import { FilterContractSearch } from '../../models/contract/filter-contract-search';
import { FilterContractSearchResult } from '../../models/contract/filter-contract-search-result';
import { FilterContractSearchResultSimplified } from '../../models/contract/filter-contract-search-result-simplified';
import { FilterContractSearchResultTotalRecord } from '../../models/contract/filter-contract-search-result-total-record';
import { Term } from '../../models/contract/term';
import { TermGeneration } from '../../models/contract/term-generation';
import { DealerApsFordDetailAttributes } from '../../models/dealer/dealer-aps-ford-detail-attributes';
import { FileType } from '../../models/enum/file-type';
import { OperationCode } from '../../models/enum/operation-code';
import { StatusContract } from '../../models/enum/status-contract';
import { ApsPermissionService } from '../../services/aps-permission/aps-permission.service';
import { ContractService } from '../../services/contract/contract.service';
import { DealerService } from '../../services/dealer/dealer.service';
import { SystemCountryService } from '../../services/system-country/system-country.service';
import { WorkbookColumn } from 'src/assets/functions/exceljs-export';

type TableLazyLoadEventLike = { first?: number | null; rows?: number | null };
type DocumentType = 'TERM' | 'TICKET';
type SearchFieldId = 'cpfCnpjSearch' | 'vinSearch' | 'contractOriginalNumberSearch';
type DealerContractExportDetail = FilterContractSearchResult & {
	modelName?: string;
	modelYear?: string;
	monthsFactoryWarranty?: number;
};
type CancellationFieldId =
	| 'cancellationDateModalSearch'
	| 'cancellationReasonModalSearch'
	| 'carMileageSearch'
	| 'dealerEmailSearch'
	| 'sellerEmailSearch'
	| 'observationsSearch';

@Component({
	selector: 'app-contract-search-dealer',
	templateUrl: './contract-search-dealer.component.html',
	styleUrls: ['./contract-search-dealer.component.scss'],
	standalone: false
})
export class ContractSearchDealerComponent implements OnInit {
	private readonly destroyRef = inject(DestroyRef);
	private readonly searchContext = 'dealer' as const;
	private readonly defaultDealerCode = '00100';
	private readonly defaultDealerName = 'FORD MOTOR COMPANY BRASIL LTDA';

	pt = {
		firstDayOfWeek: 0,
		dayNames: ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'],
		dayNamesShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'],
		dayNamesMin: ['Do', 'Se', 'Te', 'Qu', 'Qu', 'Se', 'Sa'],
		monthNames: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho',
			'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
		monthNamesShort: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
		today: 'Hoje',
		clear: 'Limpar'
	};

	es = {
		firstDayOfWeek: 0,
		dayNames: ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sábado'],
		dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'],
		dayNamesMin: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'],
		monthNames: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio',
			'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
		monthNamesShort: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
		today: 'Hoy',
		clear: 'Limpar'
	};

	calendarLang = this.pt;

	statusFilter = [
		{ label: 'TRANSLATE.ACTIVE', value: StatusContract.ACTIVE },
		{ label: 'TRANSLATE.BLOCKED', value: StatusContract.BLOCKED },
		{ label: 'TRANSLATE.CANCELED', value: StatusContract.CANCELED },
		{ label: 'TRANSLATE.FINISHED', value: StatusContract.FINISHED },
		{ label: 'TRANSLATE.CREATED', value: StatusContract.CREATED },
		{ label: 'TRANSLATE.PENDING.PAYMENT', value: StatusContract.PENDING_PAYMENT },
		{ label: 'TRANSLATE.PENDING.CANCELLATION', value: StatusContract.PENDING_CANCELLATION },
		{ label: 'TRANSLATE.PENDING.ACTIVATION', value: StatusContract.PENDING_ACTIVATION },
		{ label: 'TRANSLATE.PENDING.AMENDMENT', value: StatusContract.PENDING_AMENDMENT },
		{ label: 'TRANSLATE.WITH.ERROR', value: StatusContract.WITH_ERROR }
	];

	globalContractFilter = [
		{ label: 'TRANSLATE.SELECT', value: 0 },
		{ label: 'TRANSLATE.YES', value: 1 },
		{ label: 'TRANSLATE.NOT', value: 2 }
	];

	contractStatus = [
		StatusContract.ACTIVE,
		StatusContract.PENDING_PAYMENT
	];

	blockedUi = false;
	filterContractSearchResult: FilterContractSearchResult | null = {} as FilterContractSearchResult;
	filterContractSearch: FilterContractSearch;
	filterContractSearchResultList: FilterContractSearchResult[] = [];
	filterContractSearchResultTotalRecord: FilterContractSearchResultTotalRecord;
	isSubmitted = false;
	isSubmittedExcel = false;
	hasTriedFilterSubmit = false;
	canRequestCancellation = false;
	cdsid = '';
	nameDealer = '';

	userCountry: string;

	display = false;
	displayModalPdf = false;
	cancellationDateModal: string | null = null;
	selectedReason: string | null = null;
	idProductNameModal: string | null = null;
	chassiModal: string | null = null;
	customerNameModal: string | null = null;
	readonly DOCUMENT_TYPE_TERM: DocumentType = 'TERM';
	readonly DOCUMENT_TYPE_TICKET: DocumentType = 'TICKET';
	documentType: DocumentType | null = null;
	showDocumentTypeError = false;
	isDownloadingPdf = false;
	isSubmittingCancellation = false;
	hasTriedCancellationSubmit = false;
	prebContract = '';
	contractCountryCode = '';
	contractId!: number | null;
	termGeneration: TermGeneration | null = {} as TermGeneration;
	termFileId!: number | null;
	termTypeFile!: number | null;
	ticketFileId!: number | null;
	ticketTypeFile!: number | null;
	fileNameType: string = '';
	productKey: number = 0;
	dealerEmail: string | null = null;
	sellerEmail: string | null = null;
	sellerCode: string | null = null;
	coverageKey: number = 0;
	vinSaleDateComparison: Date = new Date();
	systemCountry: string = '';
	zeroMileage: number | null = null;
	carMileage: number | null = null;
	observations: string | null = null;
	readonly cancellationEmailMaxLength = 50;
	readonly cancellationMileageMaxLength = 7;
	readonly cancellationObservationsMaxLength = 500;
	readonly searchDocumentMaxLength = 14;
	readonly searchVinLength = 17;
	readonly searchOriginalContractLength = 10;
	private readonly emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

	cancellationReason = [
		{ label: 'TRANSLATE.WITHDRAWAL', value: ConstantsFordProtect.WITHDRAWAL },
		{ label: 'TRANSLATE.CHARGEBACK', value: ConstantsFordProtect.CHARGEBACK },
		{ label: 'TRANSLATE.THEFT', value: ConstantsFordProtect.THEFT },
		{ label: 'TRANSLATE.TOTAL.LOSS', value: ConstantsFordProtect.TOTAL_LOSS },
		{ label: 'TRANSLATE.OTHER', value: ConstantsFordProtect.OTHER }
	];

	contractStatusCode = StatusContract.PENDING_CANCELLATION;
	displayUndoCancel = false;

	contractIDUndoCancellation: number | undefined = 0;
	vinUndoCancellation: string | undefined = '';
	productIDUndoCancellation: number | undefined = 0;
	disableReactivateContract = false;
	private lastLazyLoadPage: number | null = null;
	private lastLazyLoadRows: number | null = null;
	private skipInitialLazyLoad = true;
	private skipNextLazyLoadAfterAutoSearch = false;

	constructor(
		private translateService: TranslateService,
		private messageService: MessageService,
		private router: Router,
		private apsPermissionService: ApsPermissionService,
		private contractService: ContractService,
		private dealerService: DealerService,
		private systemCountryService: SystemCountryService,
		private onlyAcceptNumbers: OnlyAcceptNumbers,
		private formatDistance: FormatOdometerNumber,
	) {

		this.filterContractSearch = new FilterContractSearch();
		this.filterContractSearchResultTotalRecord = new FilterContractSearchResultTotalRecord();
		this.userCountry = this.apsPermissionService.getUserCountry();
		this.systemCountryService.setSystemCountry();
	}

	async ngOnInit() {

		await this.apsPermissionService.bootstrapPermissions();

		this.userCountry = this.apsPermissionService.getUserCountry();
		this.systemCountry = this.systemCountryService.getSystemCountry();
		this.canRequestCancellation =
			this.apsPermissionService.permissionValidation(ApsPermitedScreen.screenContractCancellation) ||
			this.apsPermissionService.permissionValidation(ApsPermitedScreen.screenProcessContractCancel);
		const shouldRestoreFromEdit = this.shouldRestoreFromEdit();
		const savedFilter = shouldRestoreFromEdit
			? this.contractService.getFilterContractSearchByContext(this.searchContext)
			: undefined;
		const hasSavedFilter = !!savedFilter;

		if (!shouldRestoreFromEdit) {
			this.contractService.clearFilterContractSearchByContext(this.searchContext);
		}

		if (savedFilter) {
			this.filterContractSearch = savedFilter;
		}

		this.updateCalendarLanguage(this.translateService.currentLang || this.translateService.defaultLang || ConstantsFordProtect.PT_BR);

		this.translateService.onLangChange.pipe(
			takeUntilDestroyed(this.destroyRef)
		).subscribe((langChange: LangChangeEvent) => {
			this.updateCalendarLanguage(langChange.lang);
		});

		if (hasSavedFilter) {
			this.skipNextLazyLoadAfterAutoSearch = true;
			this.buttonFilterSearch();
			this.getDealerAutomatic(false);
			return;
		}

		if (!this.filterContractSearch.dealer) {
			this.filterContractSearch.dealer = this.defaultDealerCode;
		}

		if (!this.nameDealer) {
			this.nameDealer = this.defaultDealerName;
		}

		this.skipNextLazyLoadAfterAutoSearch = true;
		this.buttonFilterSearch();
		this.getDealerAutomatic(false, true);
	}

	private updateCalendarLanguage(language: string): void {
		this.calendarLang = language.toLowerCase().startsWith('es') ? this.es : this.pt;
	}

	private shouldRestoreFromEdit(): boolean {
		const state = history.state as { restoreSearch?: boolean; source?: string };
		return state?.restoreSearch === true && state?.source === 'contract-edit-dealer';
	}

	private showCancellationError(error: HttpErrorResponse, context?: Record<string, unknown>): void {
		const status = error?.status;
		const reference = this.buildCancellationErrorReference(error);
		const backendDetails = (
			error?.error?.message ||
			error?.error?.exceptionMessage ||
			error?.error?.trace ||
			error?.message ||
			''
		).toString().toLowerCase();

		const isAigNotFound =
			backendDetails.includes('aigbrasilcontractcancelexception') ||
			backendDetails.includes('não encontrado para cancelamento') ||
			backendDetails.includes('nao encontrado para cancelamento');

		const isCredentialMismatch =
			backendDetails.includes('acesso inválido') ||
			backendDetails.includes('acesso invalido') ||
			backendDetails.includes('credenciais não conferem') ||
			backendDetails.includes('credenciais nao conferem');

		if (status === 401) {
			this.clearModalCancel();
			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
			});
			return;
		}

		if (isAigNotFound) {
			const partialCancellationMessage = this.translateService.instant('TRANSLATE.CANCELLATION.PARTIAL.PROCESS.WARNING');
			const partialCancellationDetail = reference
				? `${partialCancellationMessage} ${this.translateService.instant('TRANSLATE.SUPPORT.REFERENCE')}: ${reference}.`
				: partialCancellationMessage;

			// Solicitação já registrada internamente - fechar modal e atualizar lista
			// evita que o usuário reenvie a mesma solicitação
			this.cleanFilters();
			this.filterValidation();
			this.messageService.add({
				severity: 'warn',
				summary: this.translateService.instant('TRANSLATE.ATTENTION'),
				detail: partialCancellationDetail
			});
			console.warn('Cancellation returned integration warning.', { reference, error });
			return;
		}

		if (isCredentialMismatch) {
			const supportDetail = this.appendCancellationSupportReference(
				this.translateService.instant('TRANSLATE.CANCELLATION.INTEGRATION.CREDENTIALS.ERROR'),
				reference
			);

			this.clearModalCancel();
			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: supportDetail
			});
			console.error('Cancellation rejected by integration credentials.', { reference, status, context, error });
			return;
		}

		switch (status) {
			case 400:
			case 404:
			case 500:
				this.clearEditableCancellationFields();
				this.messageService.add({
					severity: 'error',
					summary: this.translateService.instant('TRANSLATE.ERROR'),
					detail: this.appendCancellationSupportReference(
						this.translateService.instant('TRANSLATE.UNABLE.TO.REQUEST.CANCELLATION'),
						reference
					)
				});
				console.error('Cancellation request failed before completion.', { reference, status, context, error });
				break;

			default: {
				const genericErrorDetail = this.appendCancellationSupportReference(
					this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR'),
					reference
				);

				this.clearEditableCancellationFields();
				this.messageService.add({
					severity: 'error',
					summary: this.translateService.instant('TRANSLATE.ERROR'),
					detail: genericErrorDetail
				});
				console.error('Cancellation failed with unexpected error.', { reference, status, context, error });
				break;
			}
		}
	}

	clearEditableCancellationFields(): void {
		this.isSubmittingCancellation = false;
		this.hasTriedCancellationSubmit = false;
		this.cancellationDateModal = null;
		this.selectedReason = null;
		this.zeroMileage = null;
		this.carMileage = null;
		this.observations = null;
		this.dealerEmail = null;
		this.sellerEmail = null;
	}

	private appendCancellationSupportReference(detail: string, reference: string): string {
		return reference
			? `${detail} ${this.translateService.instant('TRANSLATE.SUPPORT.REFERENCE')}: ${reference}.`
			: detail;
	}

	private buildCancellationErrorReference(error: HttpErrorResponse): string {
		const backendTimestamp = error?.error?.timestamp;
		if (backendTimestamp) {
			return String(backendTimestamp);
		}

		const correlationId =
			error?.headers?.get?.('x-correlation-id') ||
			error?.headers?.get?.('x-request-id');

		if (correlationId) {
			return correlationId;
		}

		return String(Date.now());
	}

	filterValidation() {
		this.hasTriedFilterSubmit = true;

		if (this.filterContractSearch.document === '' && this.filterContractSearch.vin === ''
			&& this.filterContractSearch.dealer === '' && this.filterContractSearch.contractOriginalNumber === ''
			&& this.filterContractSearch.contractDateStart === null && this.filterContractSearch.contractDateEnd === null
			&& this.filterContractSearch.validityDateStart === null && this.filterContractSearch.validityDateEnd === null
			&& this.filterContractSearch.contractStatus === 0) {

			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.FILTER.NOT.SELECTED')
			});
		} else {
			const invalidField = this.validateSearchFilters();

			if (invalidField) {
				this.focusSearchField(invalidField);
				return;
			}

			this.buttonFilterSearch();
		}
	}

	buttonFilterSearch() {
		if (this.isSubmitted) {
			return;
		}

		if (this.validateSearchFilters()) {
			return;
		}

		const currentCountry = this.apsPermissionService.getUserCountry() || this.systemCountry;
		this.userCountry = currentCountry;
		this.filterContractSearch.countries = [];
		if (currentCountry) {
			this.filterContractSearch.countries.push(currentCountry);
		}

		this.isSubmitted = true;
		this.blockedUi = true;
		const searchPayload = this.buildSearchPayloadFromCurrentFilters();

		this.contractService.dealerContractSearch(searchPayload)
			.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response: FilterContractSearchResultTotalRecord) => {

				this.filterContractSearchResultTotalRecord = response;
				this.isSubmitted = false;
				this.blockedUi = false;
			}, (err) => {

				if (err.status === 401) {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
					});

				} else {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
					});
				}

				this.isSubmitted = false;
				this.blockedUi = false;
			});
	}

	loadData(event: TableLazyLoadEventLike | LazyLoadEvent | null | undefined) {
		if (this.isSubmitted) {
			return;
		}

		if (this.skipInitialLazyLoad) {
			this.skipInitialLazyLoad = false;
			return;
		}

		if (this.skipNextLazyLoadAfterAutoSearch) {
			this.skipNextLazyLoadAfterAutoSearch = false;
			return;
		}

		const first = event?.first ?? 0;
		const rows = event?.rows ?? this.filterContractSearch.rowsPage ?? 20;
		const nextPage = first > 0 ? (first / rows) : 0;

		// PrimeNG can emit repeated lazy events for the same page after data binding updates.
		// Ignore duplicate page/rows events to avoid request loops.
		if (this.lastLazyLoadPage === nextPage && this.lastLazyLoadRows === rows) {
			return;
		}

		this.lastLazyLoadPage = nextPage;
		this.lastLazyLoadRows = rows;
		this.filterContractSearch.numberPage = nextPage;
		this.filterContractSearch.rowsPage = rows;
		this.filterValidationLazyLoad();
	}

	filterValidationLazyLoad() {
		const hasNoFilters = this.filterContractSearch.document === '' && this.filterContractSearch.vin === ''
			&& this.filterContractSearch.dealer === '' && this.filterContractSearch.contractOriginalNumber === ''
			&& this.filterContractSearch.contractDateStart === null && this.filterContractSearch.contractDateEnd === null
			&& this.filterContractSearch.validityDateStart === null && this.filterContractSearch.validityDateEnd === null
			&& this.filterContractSearch.contractStatus === 0
			&& this.filterContractSearch.globalContract === 0;

		if (!hasNoFilters) {
			this.buttonFilterSearch();
		}
	}

	buttonClean() {

		this.filterContractSearch = new FilterContractSearch();
		this.hasTriedFilterSubmit = false;
		this.filterContractSearch.document = '';
		this.filterContractSearch.vin = '';
		this.filterContractSearch.contractOriginalNumber = '';
		this.filterContractSearch.contractDateStart = null;
		this.filterContractSearch.contractDateEnd = null;
		this.filterContractSearch.globalContract = 0;
		this.filterContractSearch.contractStatus = 0;
		this.getDealerAutomatic();
		this.filterContractSearch.countries = [];
	}

	onDocumentSearchInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		const sanitizedValue = this.sanitizeDocumentSearchValue(input.value);

		if (input.value !== sanitizedValue) {
			input.value = sanitizedValue;
		}

		this.filterContractSearch.document = sanitizedValue;
		this.hasTriedFilterSubmit = false;
	}

	onVinSearchInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		const sanitizedValue = this.normalizeAlphaNumericValue(input.value).slice(0, this.searchVinLength);

		if (input.value !== sanitizedValue) {
			input.value = sanitizedValue;
		}

		this.filterContractSearch.vin = sanitizedValue;
		this.hasTriedFilterSubmit = false;
	}

	onContractOriginalNumberInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		const sanitizedValue = this.normalizeDigitsValue(input.value).slice(0, this.searchOriginalContractLength);

		if (input.value !== sanitizedValue) {
			input.value = sanitizedValue;
		}

		this.filterContractSearch.contractOriginalNumber = sanitizedValue;
		this.hasTriedFilterSubmit = false;
	}

	private focusSearchField(fieldId: SearchFieldId): void {
		setTimeout(() => {
			document.getElementById(fieldId)?.focus();
		}, 0);
	}

	private validateSearchFilters(): SearchFieldId | null {
		if (this.isSearchDocumentInvalid()) {
			return 'cpfCnpjSearch';
		}

		if (this.isSearchVinInvalid()) {
			return 'vinSearch';
		}

		if (this.isSearchContractOriginalNumberInvalid()) {
			return 'contractOriginalNumberSearch';
		}

		return null;
	}

	isSearchDocumentInvalid(): boolean {
		const rawDocument = (this.filterContractSearch.document || '').trim();

		if (!rawDocument) {
			return false;
		}

		const normalizedDocument = this.normalizeDocumentSearchValue(rawDocument);

		if (!normalizedDocument) {
			return true;
		}

		return !this.isDocumentValidForCountry(normalizedDocument, this.getSearchDocumentCountry());
	}

	isSearchVinInvalid(): boolean {
		const vin = (this.filterContractSearch.vin || '').trim();

		if (!vin) {
			return false;
		}

		return this.normalizeAlphaNumericValue(vin).length !== this.searchVinLength;
	}

	isSearchContractOriginalNumberInvalid(): boolean {
		const contractOriginalNumber = (this.filterContractSearch.contractOriginalNumber || '').trim();

		if (!contractOriginalNumber) {
			return false;
		}

		return !/^\d{1,10}$/.test(this.normalizeDigitsValue(contractOriginalNumber));
	}

	getSearchDocumentInputMaxLength(): number {
		switch (this.getSearchDocumentCountry()) {
			case ConstantsFordProtect.BRA:
				return 18;
			case ConstantsFordProtect.ARG:
				return 11;
			case ConstantsFordProtect.CHL:
				return 9;
			case ConstantsFordProtect.COL:
				return 10;
			case ConstantsFordProtect.VEN:
				return 10;
			default:
				return this.searchDocumentMaxLength;
		}
	}

	getSearchDocumentErrorKey(): string {
		const normalizedDocument = this.normalizeDocumentSearchValue(this.filterContractSearch.document || '');

		if (this.getSearchDocumentCountry() === ConstantsFordProtect.BRA) {
			if (normalizedDocument.length === 11) {
				return 'TRANSLATE.CPF.INVALID';
			}

			if (normalizedDocument.length === 14) {
				return 'TRANSLATE.CNPJ.INVALID';
			}

			return 'TRANSLATE.FILL.CPF.CNPJ.CORRECTLY';
		}

		return 'TRANSLATE.INVALID.DOCUMENT';
	}

	private getSearchDocumentCountry(): string {
		return this.apsPermissionService.getUserCountry() || this.systemCountry;
	}

	private sanitizeDocumentSearchValue(value: string): string {
		const trimmedValue = (value || '').trimStart();
		const country = this.getSearchDocumentCountry();

		if (country === ConstantsFordProtect.BRA) {
			const normalizedDocument = this.normalizeAlphaNumericValue(trimmedValue).slice(0, this.searchDocumentMaxLength);
			return maskCpfAndCnpj(normalizedDocument).slice(0, 18);
		}

		if (country === ConstantsFordProtect.ARG) {
			return this.normalizeAlphaNumericValue(trimmedValue).slice(0, 11);
		}

		if (country === ConstantsFordProtect.CHL) {
			return this.normalizeAlphaNumericValue(trimmedValue).slice(0, 9);
		}

		if (country === ConstantsFordProtect.COL) {
			return this.normalizeAlphaNumericValue(trimmedValue).slice(0, 10);
		}

		if (country === ConstantsFordProtect.VEN) {
			return this.normalizeAlphaNumericValue(trimmedValue).slice(0, 10);
		}

		return trimmedValue.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, this.searchDocumentMaxLength);
	}

	private normalizeDocumentSearchValue(value: string): string {
		return (value || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
	}

	private normalizeAlphaNumericValue(value: string): string {
		return (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
	}

	private normalizeDigitsValue(value: string): string {
		return (value || '').replace(/\D/g, '');
	}

	private parsePositiveIdentifier(value: unknown): number {
		const normalizedValue = this.normalizeDigitsValue(String(value ?? ''));

		if (!normalizedValue) {
			return 0;
		}

		const parsedValue = Number.parseInt(normalizedValue, 10);
		return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
	}

	private buildSearchPayloadFromCurrentFilters(): FilterContractSearch {
		const currentCountry = this.apsPermissionService.getUserCountry() || this.systemCountry;
		this.userCountry = currentCountry;
		const searchPayload = this.buildNormalizedSearchPayload();
		searchPayload.countries = currentCountry ? [currentCountry] : [];
		searchPayload.operation = OperationCode.CAR;
		searchPayload.statusType = listStatusType().map(status => String(status));
		return searchPayload;
	}

	private buildExcelSearchPayload(): FilterContractSearch {
		const searchPayload = this.buildSearchPayloadFromCurrentFilters();
		const normalizedOriginalContractNumber = this.normalizeDigitsValue(this.filterContractSearch.contractOriginalNumber || '');
		if (normalizedOriginalContractNumber) {
			searchPayload.contractNumber = this.parsePositiveIdentifier(normalizedOriginalContractNumber);
			searchPayload.contractOriginalNumber = '';
		}
		searchPayload.numberPage = 0;
		if (this.filterContractSearchResultTotalRecord?.totalRecord > 0) {
			searchPayload.rowsPage = Math.max(searchPayload.rowsPage || 20, this.filterContractSearchResultTotalRecord.totalRecord);
		}
		return searchPayload;
	}

	private buildNormalizedSearchPayload(): FilterContractSearch {
		const searchPayload = { ...this.filterContractSearch } as FilterContractSearch;
		searchPayload.countries = [...(this.filterContractSearch.countries || [])];
		searchPayload.statusType = [...(this.filterContractSearch.statusType || [])];
		searchPayload.document = this.normalizeDocumentSearchValue(this.filterContractSearch.document || '');
		searchPayload.vin = this.normalizeAlphaNumericValue(this.filterContractSearch.vin || '');
		searchPayload.dealer = this.normalizeAlphaNumericValue(this.filterContractSearch.dealer || '');
		searchPayload.contractOriginalNumber = this.normalizeDigitsValue(this.filterContractSearch.contractOriginalNumber || '');
		searchPayload.contractNumber = this.parsePositiveIdentifier(searchPayload.contractOriginalNumber);

		return searchPayload;
	}

	private isDocumentValidForCountry(document: string, country: string): boolean {
		switch (country) {
			case ConstantsFordProtect.BRA:
				if (/^\d{11}$/.test(document)) {
					return validationCpf(document);
				}

				if (/^[A-Z0-9]{14}$/.test(document)) {
					return validationCnpj(document);
				}

				return false;

			case ConstantsFordProtect.ARG:
				return /^[A-Z0-9]{7,11}$/.test(document);

			case ConstantsFordProtect.CHL:
				return /^[A-Z0-9]{9}$/.test(document);

			case ConstantsFordProtect.COL:
				return /^[A-Z0-9]{4,10}$/.test(document);

			case ConstantsFordProtect.VEN:
				return /^[A-Z0-9]{8,10}$/.test(document);

			default:
				return document.length > 0;
		}
	}

	exportExcel() {
		if (this.isSubmittedExcel) {
			return;
		}

		const invalidField = this.validateSearchFilters();

		if (invalidField) {
			this.hasTriedFilterSubmit = true;
			this.focusSearchField(invalidField);
			return;
		}

		if (this.filterContractSearch.document === '' && this.filterContractSearch.vin === ''
			&& this.filterContractSearch.dealer === '' && this.filterContractSearch.contractOriginalNumber === ''
			&& this.filterContractSearch.contractDateStart === null && this.filterContractSearch.contractDateEnd === null
			&& this.filterContractSearch.validityDateStart === null && this.filterContractSearch.validityDateEnd === null
			&& this.filterContractSearch.contractStatus === 0) {

			this.messageService.add({
				severity: 'error', summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.FILTER.NOT.SELECTED')
			});

		} else {
			this.isSubmittedExcel = true;
			this.blockedUi = true;
			const searchPayload = this.buildExcelSearchPayload();

			this.contractService.dealerContractSearchExcel(searchPayload)
				.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response: ExportContractFromExcelDealer[] | ExportContractFromExcelDealer | null | undefined) => {
					const excelRows = this.normalizeExcelExportRows(response);

					if (excelRows.length > 0) {
						this.importXlsxFromArray(excelRows);
						return;
					}

					if (this.shouldAvoidExcelDetailFallback()) {
						this.handleExcelExportUnavailable();
						return;
					}

					this.importXlsxFromSearchResults(this.filterContractSearchResultTotalRecord?.filterContractSearchResultSimplifiedDtos || []);
				}, () => {
					if (this.shouldAvoidExcelDetailFallback()) {
						this.handleExcelExportUnavailable();
						return;
					}

					this.importXlsxFromSearchResults(this.filterContractSearchResultTotalRecord?.filterContractSearchResultSimplifiedDtos || []);
				});
		}
	}

	private normalizeExcelExportRows(response: ExportContractFromExcelDealer[] | ExportContractFromExcelDealer | null | undefined): ExportContractFromExcelDealer[] {
		if (Array.isArray(response)) {
			return response.filter(row => !!row);
		}

		return response ? [response] : [];
	}

	private shouldAvoidExcelDetailFallback(): boolean {
		const normalizedDealer = this.normalizeAlphaNumericValue(this.filterContractSearch?.dealer || '');
		return normalizedDealer.length > 0;
	}

	private handleExcelExportUnavailable(): void {
		this.messageService.add({
			severity: 'error',
			summary: this.translateService.instant('TRANSLATE.ERROR'),
			detail: this.translateService.instant('TRANSLATE.EXCEL.CANNOT.BE.EXPORTED')
		});

		this.isSubmittedExcel = false;
		this.blockedUi = false;
	}

	private importXlsxFromSearchResults(rows: FilterContractSearchResultSimplified[]): void {
		const normalizedRows = Array.isArray(rows) ? rows : [];

		if (normalizedRows.length === 0) {
			this.handleExcelExportUnavailable();
			return;
		}

		forkJoin(normalizedRows.map(row => this.loadExportContractDetail(row)))
			.subscribe({
				next: resolvedRows => {
					this.importXlsxFromResolvedRows(resolvedRows, normalizedRows);
				},
				error: () => {
					this.importXlsxFromSummaryRows(normalizedRows);
				}
			});
	}

	private getExportContractDetailCandidateIds(row: Partial<FilterContractSearchResultSimplified>): number[] {
		const candidateIds: number[] = [];
		const appendCandidate = (value: unknown) => {
			const identifier = this.parsePositiveIdentifier(value);

			if (identifier > 0 && !candidateIds.includes(identifier)) {
				candidateIds.push(identifier);
			}
		};

		appendCandidate(row.contractKey);
		appendCandidate(row.contractOriginalNumber);
		appendCandidate(row.contractNumber);

		return candidateIds;
	}

	private loadExportContractDetail(row: Partial<FilterContractSearchResultSimplified>): Observable<DealerContractExportDetail | null> {
		const candidateIds = this.getExportContractDetailCandidateIds(row);

		const loadCandidate = (index: number): Observable<DealerContractExportDetail | null> => {
			if (index >= candidateIds.length) {
				return of(null);
			}

			return this.contractService.searchContractId(
				candidateIds[index],
				row.productKey || 0,
				row.coverageCategoryKey || 0
			).pipe(
				switchMap((response: DealerContractExportDetail | null) => response ? of(response) : loadCandidate(index + 1)),
				catchError(() => loadCandidate(index + 1))
			);
		};

		return loadCandidate(0);
	}

	private importXlsxFromResolvedRows(
		resolvedRows: (DealerContractExportDetail | null)[],
		summaryRows: FilterContractSearchResultSimplified[]
	): void {
		const rows = summaryRows.map((summaryRow, index) => {
			const resolvedRow = resolvedRows[index];
			return resolvedRow ? this.buildExcelRowFromContractDetail(resolvedRow) : this.buildSummaryFallbackExcelRow(summaryRow);
		});

		this.exportPreparedRows(rows);
	}

	private importXlsxFromSummaryRows(summaryRows: FilterContractSearchResultSimplified[]): void {
		this.exportPreparedRows(summaryRows.map(row => this.buildSummaryFallbackExcelRow(row)));
	}

	private buildExcelRowFromContractDetail(item: DealerContractExportDetail): Record<string, unknown> {
		const currency = this.getExcelCurrency(item.country);

		return {
			[this.translateService.instant('TRANSLATE.COUNTRY.EXCEL')]: item.country || this.apsPermissionService.getUserCountry() || this.systemCountry || '',
			[this.translateService.instant('TRANSLATE.CONTRACT.NUMBER.EXCEL')]: item.contractNumber || '',
			[this.translateService.instant('TRANSLATE.FP2.CODE.EXCEL')]: item.contractCodeFp2 ?? item.contractOriginalNumber ?? '',
			[this.translateService.instant('TRANSLATE.STATUS.EXCEL')]: this.statusName(Number(item.status || 0)),
			[this.translateService.instant('TRANSLATE.VIN.EXCEL')]: item.vin || '',
			[this.translateService.instant('TRANSLATE.WSD.EXCEL')]: item.wsd || null,
			[this.translateService.instant('TRANSLATE.CONTRACT.DATE.EXCEL')]: item.contractDateCreate || null,
			[this.translateService.instant('TRANSLATE.START.DATE.EXCEL')]: item.dateStart || null,
			[this.translateService.instant('TRANSLATE.END.DATE.EXCEL')]: item.contractDateEnd || null,
			[this.translateService.instant('TRANSLATE.LAST.UPDATE.EXCEL')]: item.contractDateUpdate || null,
			[this.translateService.instant('TRANSLATE.PRODUCT.NAME.EXCEL')]: item.productName || '',
			[this.translateService.instant('TRANSLATE.LIMIT.EXCEL')]: item.limite || '',
			[this.translateService.instant('TRANSLATE.FINAL.CONTRACT.VALUE.EXCEL')]: this.formatExcelPrice(item.contractFinalPrice, currency),
			[this.translateService.instant('TRANSLATE.VIN.SELLER.PRICE.EXCEL')]: this.formatExcelPrice(item.vinSellerPrice, currency),
			[this.translateService.instant('TRANSLATE.COVERAGE.SELLER.COMMISSION.EXCEL')]: this.formatExcelPrice(item.dealerCommission, currency),
			[this.translateService.instant('TRANSLATE.CANCELLATION.REQUEST.DATE.EXCEL')]: item.cancellationRequestDate || null,
			[this.translateService.instant('TRANSLATE.CANCEL.VALUE.EXCEL')]: this.formatExcelPrice(item.cancelValue, currency),
			[this.translateService.instant('TRANSLATE.DEALER.CODE.EXCEL')]: item.dnPac || '',
			[this.translateService.instant('TRANSLATE.DEALER.NAME.EXCEL')]: item.dnName || '',
			[this.translateService.instant('TRANSLATE.DEALER.EMAIL.EXCEL')]: item.dnEmail || '',
			[this.translateService.instant('TRANSLATE.DEALER.PHONE.EXCEL')]: item.dnPhone || '',
			[this.translateService.instant('TRANSLATE.DEALER.CITY.EXCEL')]: item.dnCity || '',
			[this.translateService.instant('TRANSLATE.SELLER.CODE.EXCEL')]: item.sellerCode || '',
			[this.translateService.instant('TRANSLATE.SELLER.NAME.EXCEL')]: item.sellerName || '',
			[this.translateService.instant('TRANSLATE.SELLER.PHONE.EXCEL')]: item.sellerPhone || '',
			[this.translateService.instant('TRANSLATE.SELLER.EMAIL.EXCEL')]: item.sellerEmail || '',
			[this.translateService.instant('TRANSLATE.CLIENT.NAME.EXCEL')]: item.customerName || '',
			[this.translateService.instant('TRANSLATE.CLIENT.LAST.NAME.EXCEL')]: item.customerLastName || '',
			[this.translateService.instant('TRANSLATE.CLIENT.DOCUMENT.TYPE.EXCEL')]: item.customerDocumentType || '',
			[this.translateService.instant('TRANSLATE.CLIENT.DOCUMENT.EXCEL')]: item.customerDocument || '',
			[this.translateService.instant('TRANSLATE.CLIENT.PHONE.EXCEL')]: item.customerPhone || '',
			[this.translateService.instant('TRANSLATE.CLIENT.EMAIL.EXCEL')]: item.customerEmail || '',
			[this.translateService.instant('TRANSLATE.CLIENT.ADDRESS.EXCEL')]: item.customerAddress || '',
			[this.translateService.instant('TRANSLATE.CLIENT.ADDRESS.NUMBER.EXCEL')]: item.customerAddressNumber || '',
			[this.translateService.instant('TRANSLATE.CLIENT.ADDRESS.COMP.EXCEL')]: item.customerAddressComp || '',
			[this.translateService.instant('TRANSLATE.CLIENT.ADDRESS.DISTRICT.EXCEL')]: item.customerAddressDistrict || '',
			[this.translateService.instant('TRANSLATE.CLIENT.ADDRESS.CITY.EXCEL')]: item.customerAddressCity || '',
			[this.translateService.instant('TRANSLATE.CLIENT.ADDRESS.STATE.EXCEL')]: item.customerAddressState || '',
			[this.translateService.instant('TRANSLATE.CLIENT.CEP.EXCEL')]: item.customerCep || '',
			[this.translateService.instant('TRANSLATE.MODEL.NAME.EXCEL')]: item.modelName || '',
			[this.translateService.instant('TRANSLATE.MODEL.YEAR.EXCEL')]: item.modelYear || '',
			[this.translateService.instant('TRANSLATE.WARRANTY.MONTHS.EXCEL')]: item.monthsFactoryWarranty ?? ''
		};
	}

	private buildSummaryFallbackExcelRow(item: Partial<FilterContractSearchResultSimplified>): Record<string, unknown> {
		const contractNumbers = this.resolveFallbackContractNumbers(item);

		return {
			[this.translateService.instant('TRANSLATE.COUNTRY.EXCEL')]: item.country || this.apsPermissionService.getUserCountry() || this.systemCountry || '',
			[this.translateService.instant('TRANSLATE.CONTRACT.NUMBER.EXCEL')]: contractNumbers.contractNumber,
			[this.translateService.instant('TRANSLATE.FP2.CODE.EXCEL')]: contractNumbers.contractOriginalNumber,
			[this.translateService.instant('TRANSLATE.STATUS.EXCEL')]: this.statusName(Number(item.status || 0)),
			[this.translateService.instant('TRANSLATE.VIN.EXCEL')]: item.vin || '',
			[this.translateService.instant('TRANSLATE.PRODUCT.NAME.EXCEL')]: item.planName || '',
			[this.translateService.instant('TRANSLATE.LIMIT.EXCEL')]: item.limite || '',
			[this.translateService.instant('TRANSLATE.START.DATE.EXCEL')]: item.dateStart || null,
			[this.translateService.instant('TRANSLATE.END.DATE.EXCEL')]: item.coverageEndDate || null,
		};
	}

	private resolveFallbackContractNumbers(item: Partial<FilterContractSearchResultSimplified>): { contractNumber: string; contractOriginalNumber: string } {
		const currentSearchOriginalNumber = this.normalizeDigitsValue(this.filterContractSearch?.contractOriginalNumber || '');
		const contractNumber = String(item.contractNumber || '').trim();
		const contractOriginalNumber = String(item.contractOriginalNumber || '').trim();

		if (!contractOriginalNumber && currentSearchOriginalNumber && this.normalizeDigitsValue(contractNumber) === currentSearchOriginalNumber) {
			return {
				contractNumber: '',
				contractOriginalNumber: contractNumber
			};
		}

		return {
			contractNumber,
			contractOriginalNumber
		};
	}

	private exportPreparedRows(rows: Record<string, unknown>[]): void {
		if (rows.length === 0) {
			this.handleExcelExportUnavailable();
			return;
		}

		const excelColumns: WorkbookColumn[] = Object.keys(rows[0]).map(key => ({
			key,
			header: key
		}));

		void createWorkbookBuffer([
			{
				name: 'data',
				dataCellAlignment: { horizontal: 'right' },
				columns: excelColumns,
				rows
			}
		])
			.then(excelBuffer => {
				this.saveAsExcelFile(excelBuffer, 'contracts');
			})
			.catch(() => {
				this.handleExcelExportUnavailable();
			});
	}

	private getExcelCurrency(country: unknown): string {
		return country === ConstantsFordProtect.BRA ? 'R$' : '$';
	}

	private formatExcelPrice(value: unknown, currency: string): string {
		if (value === null || value === undefined || value === '') {
			return '';
		}

		return this.onlyAcceptNumbers.formattingFrontPrice(Number(value), currency);
	}

	private importXlsxFromArray(array: ExportContractFromExcelDealer[]): void {
		const headerMap = getHeaderMapDealer(this.translateService);
		const excelColumns = this.buildDealerExcelColumns(headerMap);

		void createWorkbookBuffer([
			{
				name: 'data',
				dataCellAlignment: { horizontal: 'right' },
				columns: excelColumns,
				rows: array.map(item => ({ ...item }))
			}
		])
			.then(excelBuffer => {
				this.saveAsExcelFile(excelBuffer, 'contracts');
			})
			.catch(() => {
				this.messageService.add({
					severity: 'error', summary: this.translateService.instant('TRANSLATE.ERROR'),
					detail: this.translateService.instant('TRANSLATE.EXCEL.CANNOT.BE.EXPORTED')
				});

				this.isSubmittedExcel = false;
				this.blockedUi = false;
			});
	}

	private saveAsExcelFile(buffer: BlobPart, fileName: string): void {

		console.log("Gerando Excel Dealer...")
		const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
		const EXCEL_EXTENSION = '.xlsx';
		const data: Blob = new Blob([buffer], {
			type: EXCEL_TYPE
		});
		saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
		this.messageService.add({
			severity: 'success',
			summary: this.translateService.instant('TRANSLATE.SUCCESS'),
			detail: this.translateService.instant('TRANSLATE.EXCEL.EXPORTED.SUCCESSFULLY')
		});
		this.isSubmittedExcel = false;
		this.blockedUi = false;
	}

	private buildDealerExcelColumns(headerMap: Record<string, string>): WorkbookColumn[] {
		const numericColumns = new Set<string>([
			'contractCodeFp2',
			'monthsFactoryWarranty',
			'contractFinalPrice',
			'vinSellerPrice',
			'dealerCommission',
			'cancelValue'
		]);
		const currencyColumns = new Set<string>([
			'contractFinalPrice',
			'vinSellerPrice',
			'dealerCommission',
			'cancelValue'
		]);
		const dateColumns = new Set<string>([
			'wsd',
			'contractDateCreate',
			'dateStart',
			'contractDateEnd',
			'contractDateUpdate',
			'cancelDate'
		]);
		const getCurrencyNumFmt = (country: unknown): string =>
			country === ConstantsFordProtect.BRA ? '[$-416]#,##0.00' : '[$-409]#,##0.00';

		return Object.entries(headerMap).map(([key, header]) => ({
			key,
			header,
			type: dateColumns.has(key) ? 'date' : numericColumns.has(key) ? 'number' : undefined,
			numFmt: dateColumns.has(key)
				? 'dd/mm/yyyy'
				: currencyColumns.has(key)
					? (row => getCurrencyNumFmt(row['country']))
					: undefined
		}));
	}

	buttonEdit(filterContractSearchResultSimplified: FilterContractSearchResultSimplified) {
		this.contractService.setFilterContractSearchByContext(this.searchContext, this.filterContractSearch);
		this.contractService.setFilterContractSearchResultSimplifiedByContext(this.searchContext, filterContractSearchResultSimplified);
		this.router.navigate(['contract-edit-dealer']);
	}

	statusName(statusId: number): string {
		return this.statusFilter.find(status => status.value === statusId)?.label ?? '';
	}

	rangeDateLabel(): string {
		const date = new Date();
		const dateLabelYear = date.getFullYear() + 50;
		return '1970:' + dateLabelYear;
	}

	getDealerAutomatic(triggerSearch = true, replaceDealerCode = false) {
		const previousDealerCode = this.filterContractSearch.dealer;

		this.dealerService.getDealerNameAndCode()
			.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response: DealerApsFordDetailAttributes) => {
				if (response === null) {
					if (!this.filterContractSearch.dealer) {
						this.filterContractSearch.dealer = this.defaultDealerCode;
					}
					this.nameDealer = this.defaultDealerName;
				} else {
					if (!this.filterContractSearch.dealer || replaceDealerCode) {
						this.filterContractSearch.dealer = response.attributeValue;
					}
					this.nameDealer = response.attributeValue;
				}

				const dealerChangedAfterResolve = previousDealerCode !== this.filterContractSearch.dealer;

				if (triggerSearch || (replaceDealerCode && dealerChangedAfterResolve)) {
					this.skipNextLazyLoadAfterAutoSearch = true;
					this.buttonFilterSearch();
				}
			});
	}

	// Modal Control - Cancellation Request
	formatOdometerNumber(event: Event) {

		const input = event.target;
		if (!(input instanceof HTMLInputElement)) {
			return;
		}
		let odometereTreatment = input.value;

		odometereTreatment = odometereTreatment.replace(/\D/g, "");
		odometereTreatment = odometereTreatment.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");

		input.value = odometereTreatment;

		const valueWithoutPoint = odometereTreatment.replace(/\./g, '').replace('.', ',');
		this.zeroMileage = valueWithoutPoint ? parseInt(valueWithoutPoint, 10) : null;
	}

	showDialog(contractKey: number, productKey: number, coverageCategoryKey: number) {
		if (!this.canRequestCancellation) {
			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION')
			});
			return;
		}

		this.clearEditableCancellationFields();
		this.idProductNameModal = null;
		this.chassiModal = null;
		this.customerNameModal = null;
		this.contractId = null;
		this.productKey = 0;
		this.coverageKey = 0;
		this.filterContractSearchResult = {} as FilterContractSearchResult;
		this.hasTriedCancellationSubmit = false;
		this.display = true;

		if (this.filterContractSearchResultTotalRecord !== null) {

			this.contractService.searchContractId(contractKey, productKey, coverageCategoryKey)
				.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response: FilterContractSearchResult) => {

					this.filterContractSearchResult = response;
					this.idProductNameModal = this.filterContractSearchResult.productKey + " - " + this.filterContractSearchResult.productName;
					this.chassiModal = this.filterContractSearchResult.vin;
					this.customerNameModal = this.filterContractSearchResult.customerName + " " + this.filterContractSearchResult.customerLastName;
					this.customerNameModal = capitalizeWords(this.customerNameModal);
					this.contractId = contractKey;
					this.productKey = productKey;
					this.coverageKey = coverageCategoryKey;
					this.vinSaleDateComparison = this.filterContractSearchResult.vinSaleDate;
					this.dealerEmail = this.filterContractSearchResult.dnEmail;
					this.sellerEmail = this.apsPermissionService.getUserEmail() || this.filterContractSearchResult.sellerEmail;
					this.sellerCode = this.filterContractSearchResult.sellerCode;

				}, (error) => {

					switch (error.status) {

						case 400:
						case 404:
						case 500:
							this.messageService.add({
								severity: 'error',
								summary: this.translateService.instant('TRANSLATE.ERROR'),
								detail: this.translateService.instant('TRANSLATE.INVALID.CONTRACT.SEARCH.CONTACT.ADM')
							});
							break;

						case 401:
							this.messageService.add({
								severity: 'error',
								summary: this.translateService.instant('TRANSLATE.ERROR'),
								detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
							});
							break;

						default:
							this.messageService.add({
								severity: 'error',
								summary: this.translateService.instant('TRANSLATE.ERROR'),
								detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
							});
							break;
					}

				});
		}
	}

	reasonChange(event: { value: string | null }) {
		this.selectedReason = event.value;
	}

	getValueLength(value: unknown): number {
		if (value === null || value === undefined) {
			return 0;
		}

		return String(value).length;
	}

	isEmailFormatInvalid(email: string | null | undefined): boolean {
		const normalizedEmail = (email || '').trim();
		return normalizedEmail.length > 0 && !this.emailPattern.test(normalizedEmail);
	}

	getCancellationMileageErrorKey(): string {
		return this.zeroMileage !== null && this.zeroMileage <= 0
			? 'TRANSLATE.MUST.BE.GREATER.THAN.ZERO'
			: 'TRANSLATE.INFORME.MILEAGE';
	}

	private focusCancellationField(fieldId: CancellationFieldId): void {
		setTimeout(() => {
			const field = this.resolveCancellationField(fieldId);
			field?.focus();
		}, 0);
	}

	private resolveCancellationField(fieldId: CancellationFieldId): HTMLElement | null {
		const field = document.getElementById(fieldId);

		if (!field) {
			return null;
		}

		if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLButtonElement) {
			return field;
		}

		return field.querySelector<HTMLElement>('.p-dropdown, [role="combobox"], .p-dropdown-label, .p-dropdown-trigger') ?? field;
	}

	private validateCancellationForm(): CancellationFieldId | null {
		if (!this.cancellationDateModal) {
			this.cancellationDateModal = null;
			return 'cancellationDateModalSearch';
		}

		const contractCreationDate = new Date(this.vinSaleDateComparison);
		const contractCancellationDate = new Date(this.cancellationDateModal);

		if (contractCancellationDate < contractCreationDate) {
			this.cancellationDateModal = null;
			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.CONTRACT.CANCELLATION.DATE.MUST.BE.AFTER.CREATION.DATE')
			});
			return 'cancellationDateModalSearch';
		}

		if (this.selectedReason === undefined || this.selectedReason === null) {
			this.selectedReason = null;
			return 'cancellationReasonModalSearch';
		}

		if (this.zeroMileage === null || Number.isNaN(this.zeroMileage)) {
			this.zeroMileage = null;
			return 'carMileageSearch';
		}

		if (this.zeroMileage <= 0) {
			return 'carMileageSearch';
		}

		if (!this.dealerEmail || this.dealerEmail.trim() === '') {
			this.dealerEmail = null;
			return 'dealerEmailSearch';
		}

		if (this.isEmailFormatInvalid(this.dealerEmail)) {
			return 'dealerEmailSearch';
		}

		if (!this.sellerEmail || this.sellerEmail.trim() === '') {
			this.sellerEmail = null;
			return 'sellerEmailSearch';
		}

		if (this.isEmailFormatInvalid(this.sellerEmail)) {
			return 'sellerEmailSearch';
		}

		if (this.observations === undefined || this.observations === null || this.observations.trim() === '') {
			this.observations = null;
			return 'observationsSearch';
		}

		return null;
	}

	cancelRequestButton() {
		if (!this.canRequestCancellation) {
			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION')
			});
			return;
		}

		this.hasTriedCancellationSubmit = true;

		const invalidField = this.validateCancellationForm();

		if (invalidField) {
			this.focusCancellationField(invalidField);
			return;
		}

		{
			const fallbackSellerCode = (sessionStorage.getItem('userId') || '').trim();
			const authenticatedSellerCode = (this.apsPermissionService.getUserCsid() || '').trim();
			const resolvedSellerCode = fallbackSellerCode || authenticatedSellerCode || (this.sellerCode || '').trim();
			const authenticatedSellerEmail = this.apsPermissionService.getUserEmail();
			const resolvedSellerEmail = (this.sellerEmail || authenticatedSellerEmail || '').trim().toLowerCase();
			const resolvedDealerEmail = (this.dealerEmail || '').trim().toLowerCase();

			if (!resolvedSellerEmail) {
				this.messageService.add({
					severity: 'error',
					summary: this.translateService.instant('TRANSLATE.ERROR'),
					detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
				});
				return;
			}

			this.isSubmittingCancellation = true;
			this.blockedUi = true;

			this.carMileage = this.zeroMileage;
			const cancellationDate = new Date(this.cancellationDateModal!);
			const formattedDate = cancellationDate.toISOString().split('T')[0]; // Format to YYYY-MM-DD

			const contractCancelInput = new ContractCancelInput();
			contractCancelInput.contractId = this.contractId!;
			contractCancelInput.productId = this.productKey;
			contractCancelInput.coverageId = this.coverageKey;
			contractCancelInput.cancelDate = formattedDate; // Use the formatted date
			contractCancelInput.cancellationReason = this.selectedReason!;
			contractCancelInput.kilometers = this.carMileage!;
			contractCancelInput.remarks = this.observations!;
			contractCancelInput.dealerEmail = resolvedDealerEmail;
			contractCancelInput.sellerEmail = resolvedSellerEmail;
			contractCancelInput.sellerCode = resolvedSellerCode;

			const cancellationDebugContext = {
				stage: 'request-cancellation',
				contractId: contractCancelInput.contractId,
				productKey: this.productKey,
				coverageKey: this.coverageKey,
				dealerEmail: resolvedDealerEmail,
				sellerEmail: contractCancelInput.sellerEmail,
				sellerCode: contractCancelInput.sellerCode,
				jwtCsid: authenticatedSellerCode,
				jwtEmail: authenticatedSellerEmail,
				sessionUserId: fallbackSellerCode,
				origin: window.location.origin
			};

			this.contractService.determineCancelValues(contractCancelInput)
				.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
					next: (cancelBody) => {
						cancelBody.sellerCode = resolvedSellerCode;
						cancelBody.createUser = resolvedSellerCode;
						cancelBody.updateUser = resolvedSellerCode;
						cancelBody.dealerEmail = contractCancelInput.dealerEmail;
						cancelBody.sellerEmail = contractCancelInput.sellerEmail;

						this.contractService.cancelContract(cancelBody)
							.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({

								next: () => {

									this.messageService.add({
										severity: 'success',
										summary: this.translateService.instant('TRANSLATE.SUCCESS'),
										detail: this.translateService.instant('TRANSLATE.CANCELLATION.REQUEST.SUCCESSFUL')
									});
									this.blockedUi = false;
									this.isSubmittingCancellation = false;
									this.cleanFilters();
									this.filterValidation();

								}, error: (error) => {

									this.blockedUi = false;
									this.isSubmittingCancellation = false;
									this.showCancellationError(error, cancellationDebugContext);

								}

							});

					}, error: (error) => {

						this.blockedUi = false;
						this.isSubmittingCancellation = false;
						this.showCancellationError(error, cancellationDebugContext);

					}
				});

		}

	}

	cleanFilters() {
		this.display = false;
		this.isSubmittingCancellation = false;
		this.hasTriedCancellationSubmit = false;
		this.cancellationDateModal = null;
		this.selectedReason = null;
		this.zeroMileage = null;
		this.carMileage = null;
		this.observations = null;
		this.dealerEmail = null;
		this.sellerEmail = null;
	}

	clearModalCancel() {

		this.display = false;
		this.isSubmittingCancellation = false;
		this.hasTriedCancellationSubmit = false;
		this.idProductNameModal = null;
		this.chassiModal = null;
		this.customerNameModal = null;
		this.contractId = null;
		this.productKey = 0;
		this.coverageKey = 0;
		this.filterContractSearchResult = null;
		this.cancellationDateModal = null;
		this.selectedReason = null;
		this.carMileage = null;
		this.zeroMileage = null;
		this.observations = null;
		this.dealerEmail = null;
		this.sellerEmail = null;
		this.sellerCode = null;
	}

	// Modal Control - Undo Cancellation
	openModalUndoCancel(contractId: number, vin: string, productId: number) {

		this.displayUndoCancel = true;
		this.contractIDUndoCancellation = contractId;
		this.vinUndoCancellation = vin;
		this.productIDUndoCancellation = productId;
	}

	undoButtonCancellation() {

		if (this.contractIDUndoCancellation && this.vinUndoCancellation && this.productIDUndoCancellation) {

			this.blockedUi = true;
			this.disableReactivateContract = true;

			// O serviço criado será implementado depois, que o mesmo já existir no backend
			this.contractService.undoCancellation(this.contractIDUndoCancellation, this.vinUndoCancellation, this.productIDUndoCancellation)
				.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
					next: () => {

						this.blockedUi = false;
						this.disableReactivateContract = false;
						this.clearFieldsUndoCancel();
						this.filterValidation();

						this.messageService.add({
							severity: 'success',
							summary: this.translateService.instant('TRANSLATE.SUCCESS'),
							detail: this.translateService.instant('TRANSLATE.REACTIVE.CONTRACT.SUCCESS')
						});
					}, error: (error) => {

						this.blockedUi = false;
						this.disableReactivateContract = false;
						switch (error.status) {

							case 400:
							case 404:
							case 500:

								this.messageService.add({
									severity: 'error',
									summary: this.translateService.instant('TRANSLATE.ERROR'),
									detail: this.translateService.instant('TRANSLATE.REACTIVE.CONTRACT.ERROR')
								});
								break;

							case 401:

								this.messageService.add({
									severity: 'error',
									summary: this.translateService.instant('TRANSLATE.ERROR'),
									detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
								});
								break;

							default:

								this.messageService.add({
									severity: 'error',
									summary: this.translateService.instant('TRANSLATE.ERROR'),
									detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
								});
								break;
						}
					}
				});

		}

	}

	clearFieldsUndoCancel() {
		this.displayUndoCancel = false;
		this.contractIDUndoCancellation = undefined;
		this.vinUndoCancellation = undefined;
		this.productIDUndoCancellation = undefined;
	}

	// Modal Control - PDF
	retrievingContractId(contractNumber: number, _country?: string, _productCode?: string) {
		this.contractId = contractNumber;
		this.contractCountryCode = _country ?? '';
		this.prebContract = _productCode ?? '';
		this.termFileId = null;
		this.termTypeFile = null;
		this.ticketFileId = null;
		this.ticketTypeFile = null;
		this.documentType = null;
		this.showDocumentTypeError = false;
	}

	searchingTermByContractIdAndFileType(contractId: number) {

		return this.contractService.searchByContractIdAndFileType(contractId, this.getTermFileTypesForSelectedContract())
			.pipe(
				tap((response: Term | null) => {
					this.termFileId = response?.fileId ?? null;
					this.termTypeFile = response?.fileType ?? null;
				}),
				catchError(() => {
					this.termFileId = null;
					this.termTypeFile = null;
					return of(null);
				})
			);

	}

	searchingForTicketByContractIdAndFileType(contractId: number) {

		return this.contractService.searchByContractIdAndFileType(contractId, fileTypeTicket().map(ft => ft as number))
			.pipe(
				tap((response: Term | null) => {
					this.ticketFileId = response?.fileId ?? null;
					this.ticketTypeFile = response?.fileType ?? null;
				}),
				catchError(() => {
					this.ticketFileId = null;
					this.ticketTypeFile = null;
					return of(null);
				})
			);

	}

	showModalPdf() {

		if (this.hasValidContractId()) {

			this.blockedUi = true;
			this.showDocumentTypeError = false;
			forkJoin([
				this.searchingTermByContractIdAndFileType(this.contractId as number),
				this.searchingForTicketByContractIdAndFileType(this.contractId as number)
			])
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
					next: () => {
						this.blockedUi = false;
						this.displayModalPdf = true;
					},
					error: () => {
						this.blockedUi = false;
						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
						});
					}
				});
			return;
		}

		this.messageService.add({
			severity: 'error',
			summary: this.translateService.instant('TRANSLATE.ERROR'),
			detail: this.translateService.instant('TRANSLATE.CONTRACT.NOT.FOUND')
		});
	}

	handleGeneratePdf(): void {
		if (this.shouldOpenPdfModal()) {
			this.showModalPdf();
			return;
		}

		this.downloadTermImmediately();
	}

	opDocument(documentType: DocumentType | null) {
		this.documentType = documentType;
		this.showDocumentTypeError = false;
	}

	private hasValidContractId(): boolean {
		return this.contractId !== null && this.contractId > 0;
	}

	private hasTermFileAvailable(): boolean {
		return this.termFileId !== null && this.termFileId > 0;
	}

	hasTermDownloadOption(): boolean {
		return this.hasTermFileAvailable() || (this.shouldDownloadTermByContractId() && this.hasValidContractId());
	}

	private hasTicketFileAvailable(): boolean {
		return this.ticketFileId !== null && this.ticketFileId > 0;
	}

	private getTermFileTypesForSelectedContract(): number[] {
		switch (this.contractCountryCode) {
			case ConstantsFordProtect.BRA:
				return [
					FileType.PURCHASE_TERM as number,
					FileType.LICENSE_TERM as number,
					FileType.CONTRACT_SUCCESS_ADHESION_DEALER_MAINTENANCE as number,
				];
			case ConstantsFordProtect.ARG:
				return [
					FileType.CONTRACT_SUCCESS_ARG as number,
					FileType.CONTRACT_SUCCESS_DEALER_MAINTENANCE_ARGENTINA as number,
				];
			case ConstantsFordProtect.CHL:
				return [FileType.CONTRACT_SUCCESS_CH as number];
			case ConstantsFordProtect.COL:
				return [FileType.CONTRACT_SUCCESS_CO as number];
			default:
				return [FileType.PURCHASE_TERM as number, FileType.LICENSE_TERM as number];
		}
	}

	private isContractIdTermFileType(fileType: number | null): boolean {
		return fileType === FileType.CONTRACT_SUCCESS_ARG
			|| fileType === FileType.CONTRACT_SUCCESS_ADHESION_DEALER_MAINTENANCE
			|| fileType === FileType.CONTRACT_SUCCESS_DEALER_MAINTENANCE_ARGENTINA;
	}

	private isSelectedPrebContract(): boolean {
		return this.prebContract === ConstantsFordProtect.PREB;
	}

	private isBrazilPrebContract(): boolean {
		return this.contractCountryCode === ConstantsFordProtect.BRA && this.isSelectedPrebContract();
	}

	private shouldOpenPdfModal(): boolean {
		return this.isBrazilPrebContract();
	}

	private shouldDownloadTermByContractId(): boolean {
		return this.isSelectedPrebContract() || this.isContractIdTermFileType(this.termTypeFile);
	}

	private hasAnyDownloadableDocument(): boolean {
		return this.hasTermFileAvailable() || this.hasTicketFileAvailable();
	}

	private downloadTermImmediately(): void {
		if (this.blockedUi) {
			return;
		}

		if (!this.hasValidContractId()) {
			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.CONTRACT.NOT.FOUND')
			});
			return;
		}

		this.showDocumentTypeError = false;
		this.isDownloadingPdf = true;
		this.blockedUi = true;

		this.searchingTermByContractIdAndFileType(this.contractId as number)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					if (!this.hasTermDownloadOption()) {
						this.showUnavailableDocumentMessage(this.DOCUMENT_TYPE_TERM);
						this.isDownloadingPdf = false;
						this.blockedUi = false;
						return;
					}

					this.downloadResolvedTerm();
				},
				error: () => {
					this.isDownloadingPdf = false;
					this.blockedUi = false;
					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
					});
				}
			});
	}

	private downloadResolvedTerm(): void {
		if (this.shouldDownloadTermByContractId()) {
			this.downloadContractIdTerm();
			return;
		}

		this.termGeneration = new TermGeneration(this.contractId as number, this.termFileId as number);

		this.contractService.createFileTerm(this.termGeneration)
			.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
				next: (blob) => {
					const fileName = this.getFileName(this.termTypeFile ?? 0);
					this.forceDownload(blob, fileName);

					this.displayModalPdf = false;
					this.clearModalPdf();

					this.messageService.add({
						severity: 'success',
						summary: this.translateService.instant('TRANSLATE.SUCCESS'),
						detail: this.translateService.instant('TRANSLATE.DOWNLOAD.TERM.SUCCESSFULLY')
					});
					this.isDownloadingPdf = false;
					this.blockedUi = false;
				}, error: (error: HttpErrorResponse) => {

					this.clearModalPdf();
					this.showDownloadError(error, this.DOCUMENT_TYPE_TERM);
					this.isDownloadingPdf = false;
					this.blockedUi = false;
				}
			});
	}

	downloadPdf() {
		// Prevent concurrent downloads
		if (this.blockedUi) {
			return;
		}

		if (!this.documentType) {
			this.documentType = null;
			this.showDocumentTypeError = true;
			this.isDownloadingPdf = false;
			return;
		}

		this.showDocumentTypeError = false;
		this.isDownloadingPdf = true;

		this.blockedUi = true;

		if (this.documentType === this.DOCUMENT_TYPE_TERM) {
			if (!this.hasValidContractId() || !this.hasTermDownloadOption()) {
				this.showUnavailableDocumentMessage(this.DOCUMENT_TYPE_TERM);
				this.isDownloadingPdf = false;
				this.blockedUi = false;
				return;
			}

			this.downloadResolvedTerm();

		} else if (this.documentType === this.DOCUMENT_TYPE_TICKET) {
			if (!this.hasValidContractId() || !this.hasTicketFileAvailable()) {
				this.showUnavailableDocumentMessage(this.DOCUMENT_TYPE_TICKET);
				this.isDownloadingPdf = false;
				this.blockedUi = false;
				return;
			}

			this.contractService.getPdfFile(this.contractId as number, this.ticketFileId as number)
				.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
					next: (blob) => {
						const fileName = this.getFileName(this.ticketTypeFile ?? 0);
						this.forceDownload(blob, fileName);

						this.displayModalPdf = false;
						this.clearModalPdf();

						this.messageService.add({
							severity: 'success',
							summary: this.translateService.instant('TRANSLATE.SUCCESS'),
							detail: this.translateService.instant('TRANSLATE.DOWNLOAD.TICKET.SUCCESSFULLY')
						});
						this.isDownloadingPdf = false;
						this.blockedUi = false;
					},
					error: (error: HttpErrorResponse) => {
						this.showDownloadError(error, this.DOCUMENT_TYPE_TICKET);
						this.isDownloadingPdf = false;
						this.blockedUi = false;
					}
				});
		} else {
			this.documentType = null;
			this.isDownloadingPdf = false;
			this.blockedUi = false;
		}
	}

	private getDocumentTypeLabel(documentType: DocumentType): string {
		return documentType === this.DOCUMENT_TYPE_TERM
			? this.translateService.instant('TRANSLATE.TERM')
			: this.translateService.instant('TRANSLATE.TICKET');
	}

	private showUnavailableDocumentMessage(documentType: DocumentType): void {
		const documentLabel = this.getDocumentTypeLabel(documentType);
		this.messageService.add({
			severity: 'error',
			summary: this.translateService.instant('TRANSLATE.ERROR'),
			detail: `${documentLabel} não está disponível para este contrato.`
		});
	}

	private showDownloadError(error: HttpErrorResponse, documentType: DocumentType): void {
		if (error.status === 401) {
			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
			});
			return;
		}

		if (error.status === 400 || error.status === 404 || error.status === 500) {
			this.showUnavailableDocumentMessage(documentType);
			return;
		}

		this.messageService.add({
			severity: 'error',
			summary: this.translateService.instant('TRANSLATE.ERROR'),
			detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
		});
	}

	private downloadContractIdTerm(): void {
		this.contractService.downloadFile(this.contractId as number)
			.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
				next: (response) => {
					const fileName = this.termTypeFile !== null
						? this.getFileName(this.termTypeFile)
						: FileType.CONTRACT_TERMS_NAME;
					if (!this.forceBase64Download(response.pdfByteArray, fileName)) {
						this.clearModalPdf();
						this.showInvalidPdfSourceError();
						this.isDownloadingPdf = false;
						this.blockedUi = false;
						return;
					}

					this.displayModalPdf = false;
					this.clearModalPdf();

					this.messageService.add({
						severity: 'success',
						summary: this.translateService.instant('TRANSLATE.SUCCESS'),
						detail: this.translateService.instant('TRANSLATE.DOWNLOAD.TERM.SUCCESSFULLY')
					});
					this.isDownloadingPdf = false;
					this.blockedUi = false;
				},
				error: (error: HttpErrorResponse) => {
					this.clearModalPdf();
					this.showDownloadError(error, this.DOCUMENT_TYPE_TERM);
					this.isDownloadingPdf = false;
					this.blockedUi = false;
				}
			});
	}

	isDownloadFileTerm(): boolean {
		return this.contractId !== null && this.termFileId !== null && this.contractId > 0 && this.termFileId > 0;
	}

	downloadFileTerm() {

		// Prevent concurrent downloads
		if (this.blockedUi) {
			return;
		}

		if (
			this.contractId !== null &&
			this.termFileId !== null &&
			this.contractId > 0 &&
			this.termFileId > 0
		) {
			this.termGeneration = new TermGeneration(this.contractId as number, this.termFileId as number);
			this.blockedUi = true;

			this.contractService.createFileTerm(this.termGeneration).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
				(blob) => {
					const fileName = this.getFileName(this.termTypeFile ?? 0);
					this.forceDownload(blob, fileName);

					this.displayModalPdf = false;
					this.clearModalPdf();

					this.messageService.add({
						severity: 'success',
						summary: this.translateService.instant('TRANSLATE.SUCCESS'),
						detail: this.translateService.instant('TRANSLATE.DOWNLOAD.TERM.SUCCESSFULLY'),
					});
					this.blockedUi = false;
				},
				(error) => {
					switch (error.status) {
						case 400:
						case 404:
						case 500:
							this.messageService.add({
								severity: 'error',
								summary: this.translateService.instant('TRANSLATE.ERROR'),
								detail: this.translateService.instant('TRANSLATE.DOWNLOAD.COULD.NOT.BE.COMPLETED'),
							});
							break;
						case 401:
							this.messageService.add({
								severity: 'error',
								summary: this.translateService.instant('TRANSLATE.ERROR'),
								detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE'),
							});
							break;
						default:
							this.messageService.add({
								severity: 'error',
								summary: this.translateService.instant('TRANSLATE.ERROR'),
								detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR'),
							});
							break;
					}
					this.blockedUi = false;
				}
			);
		} else {
			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.CONTRACT.NOT.FOUND'),
			});
		}
	}

	downloadTermInPdf() {

		// Prevent concurrent downloads
		if (this.blockedUi) {
			return;
		}

		if (!this.isDownloadFileTerm()) {
			this.showUnavailableDocumentMessage(this.DOCUMENT_TYPE_TERM);
			return;
		}

		this.blockedUi = true;
		this.termGeneration = new TermGeneration(this.contractId as number, this.termFileId as number);

		this.contractService.createFileTerm(this.termGeneration)
			.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
				(blob) => {

					const fileName = this.getFileName(this.termTypeFile ?? 0);
					this.forceDownload(blob, fileName);

					this.clearModalPdf();

					this.messageService.add({
						severity: 'success',
						summary: this.translateService.instant('TRANSLATE.SUCCESS'),
						detail: this.translateService.instant('TRANSLATE.DOWNLOAD.TERM.SUCCESSFULLY')
					});

					this.blockedUi = false;
				}, (error: HttpErrorResponse) => {

					this.clearModalPdf();
					this.showDownloadError(error, this.DOCUMENT_TYPE_TERM);
					this.blockedUi = false;
				}
			);
	}

	getFileName(typeFile: number): string {

		switch (typeFile) {

			case FileType.PURCHASE_TERM:
				return FileType.PURCHASE_TERM_NAME;

			case FileType.LICENSE_TERM:
				return FileType.LICENSE_TERM_NAME;

			case FileType.TICKET:
				return FileType.TICKET_NAME;

			case FileType.UPDATE_TICKET:
				return FileType.UPDATE_TICKET_NAME;

			case FileType.CANCELLATION_TICKET:
				return FileType.CANCELLATION_TICKET_NAME;

			case FileType.CONTRACT_SUCCESS_CO:
				return FileType.CONTRACT_SUCCESS_CO_NAME;

			case FileType.CONTRACT_SUCCESS_CH:
				return FileType.CONTRACT_SUCCESS_CH_NAME;

			case FileType.CONTRACT_SUCCESS_ARG:
				return FileType.CONTRACT_TERMS_NAME_ARG;

			case FileType.CONTRACT_SUCCESS_ADHESION_DEALER_MAINTENANCE:
				return FileType.CONTRACT_SUCCESS_ADHESION_DEALER_MAINTENANCE_NAME;

			case FileType.CONTRACT_SUCCESS_DEALER_MAINTENANCE_ARGENTINA:
				return FileType.CONTRACT_SUCCESS_DEALER_MAINTENANCE_ARGENTINA_NAME;

			default:
				return 'arquivo_generico.pdf';

		}

	}

	forceDownload(blob: Blob, fileName: string) {

		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = fileName;
		link.click();
		window.URL.revokeObjectURL(url);
	}

	private forceBase64Download(source: string, fileName: string): boolean {
		const blob = this.base64PdfToBlob(source);
		if (!blob) {
			return false;
		}

		this.forceDownload(blob, fileName);
		return true;
	}

	private base64PdfToBlob(source: string): Blob | null {
		const normalizedSource = source.trim().replace(/^['"]|['"]$/g, '').replace(/\s+/g, '');
		const dataUrlMatch = normalizedSource.match(/^data:[^;,]+;base64,([^#?]+)$/i);
		const rawBase64Payload = dataUrlMatch?.[1]
			?? (/^[A-Za-z0-9+/=_-]+$/.test(normalizedSource) ? normalizedSource : null);
		const base64Payload = rawBase64Payload
			? this.normalizeBase64Payload(rawBase64Payload)
			: null;

		if (!base64Payload) {
			return null;
		}

		let binary = '';
		try {
			binary = window.atob(base64Payload);
		} catch {
			return null;
		}

		const bytes = new Uint8Array(binary.length);

		for (let index = 0; index < binary.length; index += 1) {
			bytes[index] = binary.charCodeAt(index);
		}

		if (!binary.startsWith('%PDF')) {
			return null;
		}

		return new Blob([bytes], { type: 'application/pdf' });
	}

	private normalizeBase64Payload(source: string): string | null {
		const normalized = source.replace(/-/g, '+').replace(/_/g, '/');
		const remainder = normalized.length % 4;

		if (remainder === 1) {
			return null;
		}

		return remainder === 0
			? normalized
			: normalized.padEnd(normalized.length + (4 - remainder), '=');
	}

	private showInvalidPdfSourceError(): void {
		this.messageService.add({
			severity: 'error',
			summary: this.translateService.instant('TRANSLATE.ERROR'),
			detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
		});
	}

	clearModalPdf() {

		this.displayModalPdf = false;
		this.documentType = null;
		this.showDocumentTypeError = false;
		this.isDownloadingPdf = false;

		this.contractId = null;
		this.termFileId = null;
		this.termTypeFile = null;
		this.termGeneration = null;
		this.ticketFileId = null;
		this.ticketTypeFile = null;
		this.fileNameType = '';
		this.contractCountryCode = '';
		this.prebContract = '';
	}

}
